import { useEffect, useRef, useState } from 'react'
import {
    getSettings, saveSettings, type Settings as SettingsT,
    getBrandConfigs, saveBrandConfig, setupBrandStripeWebhook, clearSettingsSecret, clearBrandSecret,
    type BrandConfig, type RefundFeeOverride, type FeeType,
    listLabelPresets, type LabelPreset,
} from '../api'
import { useDirtyGuard, stableJson, type SaveResult } from '../layout/TabsContext'
import { useToast } from '../layout/ToastContext'
import { useAuth } from '../layout/AuthContext'
import Panel from '../components/Panel'
import InfoTip from '../components/InfoTip'
import SecretInput from '../components/SecretInput'
import CountryPicker from '../components/CountryPicker'
import ConfirmDialog from '../components/ConfirmDialog'
import { checkBeforeSave } from '../forms/checkForm'
import { knownCurrencies } from '../../shared/currency'
import { moneySign, labelPresetText, envelopeText } from '../lookups'
import { fill, textOf, LANGUAGE, LABEL_PRESET_NAMES, CURRENCY_SETTING_TEXTS } from '../../shared/texts'
import { PAGES_SETTINGS_TEXTS as T } from '../../shared/texts'
import { ABO_RUN_HOUR_MAX, BRAND_CONFIG_DEFAULTS as BD, ENVELOPE_FORMATS, INGEST_POLL_HOURS_MAX, LOG_RETENTION_DAYS_MAX, PAYMENT_AUTO_IMPORT_DAY_MAX, AUTHOR_BRAND, SETTINGS_DEFAULTS as D } from '../../shared/constants'

// Which pane the single pill row shows: the general settings, or one brand. `selectedBrand`
// (below) stays the brand whose config sits in brandForm — it is NOT cleared on a detour to
// "Allgemein", so the loaded brand survives and one save still persists both halves.
const GENERAL = 'general'

// Logging vocabulary (mirrors server utils/logModules.ts) for the Protokollierung panel.
const LOG_LEVELS = ['fatal', 'error', 'warn', 'info', 'debug'] as const
const LOG_MODULES: { key: string; label: string }[] = [
    { key: 'system', label: T.logModules.system },
    { key: 'auth', label: T.logModules.auth },
    { key: 'webhook', label: T.logModules.webhook },
    { key: 'stripe', label: T.logModules.stripe },
    { key: 'paypal', label: T.logModules.paypal },
    { key: 'billing', label: T.logModules.billing },
    { key: 'ingest', label: T.logModules.ingest },
    { key: 'export', label: T.logModules.export },
    { key: 'mail', label: T.logModules.mail },
    { key: 'scheduler', label: T.logModules.scheduler },
]

// Per-brand editable form (secrets blank, only overwrite when typed).
const emptyBrandForm = {
    paymentProvider: BD.paymentProvider,
    stripeTestmode: BD.stripeTestmode,
    stripeTestKey: '', stripeLiveKey: '',
    stripeTestWebhookSecret: '', stripeLiveWebhookSecret: '',
    mollieTestmode: BD.mollieTestmode,
    mollieTestKey: '', mollieLiveKey: '',
    vitalpilzeWebhookUrl: '', vitalpilzeWebhookSecret: '',
    refundFeeType: BD.feeType as FeeType, refundFeeValue: null as number | null,
    refundFeeOverrides: [] as RefundFeeOverride[],
    authorFeeType: BD.feeType as FeeType, authorFeeValue: null as number | null, authorPayoutThreshold: null as number | null, chargebackFeeFlat: null as number | null,
    issuerName: '', issuerAddress: '', issuerTaxNumber: '', issuerVatId: '', issuerRegister: '',
    issuerStreet: '', issuerPostalCode: '', issuerCity: '', issuerCountry: '', issuerEmail: '',
    impressumUrl: '', issuerPhone: '', brandDisplayName: '', contactEmail: '', hotline: '',
    mailFromAddress: '', mailFromName: '',
    smtpHost: '', smtpPort: null as number | null, smtpSecure: false, smtpUser: '', smtpPass: '',
}

// ISO 4217 codes with their German names — from the platform, not a table here.
const currencyNames = new Intl.DisplayNames(LANGUAGE, { type: 'currency' })
const currencyOptions = knownCurrencies().map((code) => ({ code, name: currencyNames.of(code) ?? code }))

// Every brand field inside the panel "Dozenten-Abrechnung — {brand}", which the author switch hides as a whole.
// ⚠️ A field added to or moved out of that panel belongs here too.
const AUTHOR_PANEL_FIELDS = new Set([
    'authorFeeType', 'authorFeeValue', 'authorPayoutThreshold', 'chargebackFeeFlat',
    'issuerName', 'issuerTaxNumber', 'issuerRegister', 'issuerVatId', 'issuerAddress', 'issuerStreet',
    'issuerPostalCode', 'issuerCity', 'issuerCountry', 'issuerEmail', 'issuerPhone',
    'brandDisplayName', 'contactEmail', 'hotline', 'impressumUrl',
    'mailFromAddress', 'mailFromName', 'smtpHost', 'smtpPort', 'smtpSecure', 'smtpUser', 'smtpPass',
])

export default function Settings({ tabId }: { tabId?: string }) {
    const formRef = useRef<HTMLFormElement>(null)
    const { toast } = useToast()
    const { can, refreshUser } = useAuth()
    const readOnly = !can('settings', 'write') // role lacks settings:write → view only
    const [showBrand, setShowBrand] = useState(false)
    const [s, setS] = useState<SettingsT | null>(null)
    const [error, setError] = useState('')
    const [saving, setSaving] = useState(false)

    // Editable form state. Secret fields start empty and only overwrite when typed.
    const [form, setForm] = useState({
        stripeTestmode: D.stripeTestmode,
        stripeTestKey: '', stripeLiveKey: '',
        stripeTestWebhookSecret: '', stripeLiveWebhookSecret: '',
        converterUrl: '', converterWebhookSecret: '',
        checkoutApiKey: '',
        paypalTestmode: D.paypalTestmode,
        paypalTestClientId: '', paypalTestSecret: '', paypalLiveClientId: '', paypalLiveSecret: '',
        mollieBalanceToken: '',
        taxAdvisorEmail: '', taxAdvisorBcc: '',
        smtpHost: '', smtpPort: D.smtpPort, smtpSecure: D.smtpSecure, smtpUser: '', smtpPass: '', smtpFrom: '', testMailTo: '', testMailMode: D.testMailMode,
        imapHost: '', imapPort: D.imapPort, imapSecure: D.imapSecure, imapUser: '', imapPass: '',
        imapMailbox: D.imapMailbox, imapTrashMailbox: D.imapTrashMailbox, ingestNotifyEmail: '', adminNotifyEmail: '', ingestEnabled: D.ingestEnabled,
        publicBaseUrl: '',
        llmEnabled: D.llmEnabled, llmUrl: '', llmModel: D.llmModel, llmTimeoutMs: D.llmTimeoutMs,
        aboRunHour: D.aboRunHour, ingestPollHours: D.ingestPollHours, paymentAutoImportEnabled: D.paymentAutoImportEnabled, paymentAutoImportDay: D.paymentAutoImportDay, paymentAutoImportTime: D.paymentAutoImportTime,
        logLevelDefault: D.logLevelDefault as string, logLevelOverrides: {} as Record<string, string>, logRetentionDays: D.logRetentionDays,
        certSenderName: '', certSenderLine1: '', certSenderLine2: '',
        certSenderPostalCode: '', certSenderCity: '', certSenderCountry: D.certSenderCountry,
        certAddressOutput: D.certAddressOutput,
        certLabelPreset: 'custom',
        certLabelFillDirection: D.certLabelFillDirection,
        certLabelWidthMm: D.certLabelWidthMm, certLabelHeightMm: D.certLabelHeightMm, certLabelColumns: D.certLabelColumns, certLabelRows: D.certLabelRows,
        certLabelMarginTopMm: D.certLabelMarginTopMm, certLabelMarginLeftMm: D.certLabelMarginLeftMm, certLabelGapXMm: D.certLabelGapXMm, certLabelGapYMm: D.certLabelGapYMm,
        certLabelStartPosition: D.certLabelStartPosition,
        certEnvelopeFormat: D.certEnvelopeFormat,
        certErrorNotifyTo: '',
        certNotifyMailEnabled: D.certNotifyMailEnabled, certNotifyMailTo: '',
        certNotifyTelegramEnabled: D.certNotifyTelegramEnabled, certTelegramBotToken: '', certTelegramChatIds: '',
        sepaCreditorName: '', sepaIban: '', sepaBic: '',
        currency: '',
    })
    const [baseline, setBaseline] = useState<string | null>(null) // JSON of last loaded/saved form
    // stableJson, not JSON.stringify: logLevelOverrides is keyed by module, and a
    // level set back to "Standard" and chosen again lands at the end of that map.
    const formDirty = baseline !== null && stableJson(form) !== baseline

    // Per-brand config (own Stripe account + converter wiring).
    const [brands, setBrands] = useState<BrandConfig[]>([])
    // „Dozenten-Abrechnung aktiv" per brand. Stored in the brand config, but switched in the
    // general pane (operator, 2026-10-23) — so it counts towards the GENERAL pane's unsaved
    // state, and its baseline is the loaded brand list itself.
    const [authorOn, setAuthorOn] = useState<Record<string, boolean>>({})
    const authorOnOf = (list: BrandConfig[]) => Object.fromEntries(list.map((b) => [b.brand, b.authorSettlementEnabled !== false]))
    const authorChanged = brands.filter((b) => (authorOn[b.brand] ?? true) !== (b.authorSettlementEnabled !== false))
    const globalDirty = formDirty || authorChanged.length > 0
    const [selectedBrand, setSelectedBrand] = useState('')
    const [brandForm, setBrandForm] = useState({ ...emptyBrandForm })
    const [brandBaseline, setBrandBaseline] = useState<string | null>(null)
    const [brandWebhookBusy, setBrandWebhookBusy] = useState(false)
    // Etikettenkatalog vom Server — eine Quelle für Maße und Raster.
    const [labelPresets, setLabelPresets] = useState<LabelPreset[]>([])
    useEffect(() => { listLabelPresets().then(setLabelPresets).catch(() => setLabelPresets([])) }, [])
    const activePreset = labelPresets.find((p) => p.key === form.certLabelPreset) ?? null
    const selBrand = brands.find((b) => b.brand === selectedBrand) ?? null
    // As SAVED — the switch lives in the general pane, which cannot be dirty while a brand is open.
    const authorsOff = selBrand?.authorSettlementEnabled === false
    const brandFormDirty = brandBaseline !== null && JSON.stringify(brandForm) !== brandBaseline
    const brandDirty = brandFormDirty

    // Required fields the server found empty in the SAVED config (ZUGFeRD seller, conditions).
    // Which ones is the server's answer, never a list here; a field loses its mark as soon as
    // something is typed, the notice above the mask once it is saved.
    const R = T.requirements
    const requiredGroups = [...(selBrand?.requirements?.zugferd ?? []), ...(selBrand?.requirements?.conditions ?? [])]
    // Display only: with author settlement off the notice names no field of the hidden author panel —
    // a group goes once all its fields sit there, a line once nothing is left. Server and endpoint still report them.
    const inHiddenPanel = (f: string) => authorsOff && AUTHOR_PANEL_FIELDS.has(f)
    const shownGroups = (gs: string[][] | undefined) => (gs ?? []).filter((g) => !g.every(inHiddenPanel))
    const zugferdShown = shownGroups(selBrand?.requirements?.zugferd)
    const conditionsShown = shownGroups(selBrand?.requirements?.conditions)
    // Only real gaps are shown (Betreiber, 02.11.2026: „Hinweise ausblenden. Nur Fehler anzeigen“) —
    // the server still reports the empty optional fields in requirements.optional, the page no longer
    // prints them.
    const isEmpty = (f: string) => { const v = (brandForm as Record<string, unknown>)[f]; return v == null || v === '' }
    function mark(key: keyof typeof brandForm, style?: React.CSSProperties) {
        const missing = requiredGroups.some((g) => g.includes(key) && g.every(isEmpty))
        return { className: missing ? 'field is-missing' : 'field', title: missing ? R.fieldTitle : undefined, style }
    }
    const fieldList = (groups: string[][]) =>
        groups.map((g) => g.map((f) => R.fields[f] ?? f).join(R.alternative)).join(R.separator)

    const dirty = globalDirty || brandDirty

    function apply(data: SettingsT) {
        setS(data)
        const next = {
            stripeTestmode: data.stripeTestmode,
            stripeTestKey: '', stripeLiveKey: '',
            stripeTestWebhookSecret: '', stripeLiveWebhookSecret: '',
            converterUrl: data.converterUrl, converterWebhookSecret: '',
            checkoutApiKey: '',
            paypalTestmode: data.paypalTestmode,
            paypalTestClientId: data.paypalTestClientId, paypalTestSecret: '',
            paypalLiveClientId: data.paypalLiveClientId, paypalLiveSecret: '',
            mollieBalanceToken: '',
            taxAdvisorEmail: data.taxAdvisorEmail, taxAdvisorBcc: data.taxAdvisorBcc ?? '',
            smtpHost: data.smtpHost, smtpPort: data.smtpPort, smtpSecure: data.smtpSecure,
            smtpUser: data.smtpUser, smtpPass: '', smtpFrom: data.smtpFrom, testMailTo: data.testMailTo ?? '', testMailMode: !!data.testMailMode,
            imapHost: data.imapHost, imapPort: data.imapPort, imapSecure: data.imapSecure,
            imapUser: data.imapUser, imapPass: '', imapMailbox: data.imapMailbox,
            imapTrashMailbox: data.imapTrashMailbox, ingestNotifyEmail: data.ingestNotifyEmail,
            adminNotifyEmail: data.adminNotifyEmail ?? '',
            ingestEnabled: data.ingestEnabled, publicBaseUrl: data.publicBaseUrl,
            llmEnabled: data.llmEnabled, llmUrl: data.llmUrl, llmModel: data.llmModel, llmTimeoutMs: data.llmTimeoutMs,
            aboRunHour: data.aboRunHour, ingestPollHours: data.ingestPollHours,
            paymentAutoImportEnabled: data.paymentAutoImportEnabled, paymentAutoImportDay: data.paymentAutoImportDay,
            paymentAutoImportTime: data.paymentAutoImportTime,
            logLevelDefault: data.logLevelDefault, logLevelOverrides: { ...data.logLevelOverrides },
            logRetentionDays: data.logRetentionDays,
            certSenderName: data.certSenderName ?? '', certSenderLine1: data.certSenderLine1 ?? '',
            certSenderLine2: data.certSenderLine2 ?? '', certSenderPostalCode: data.certSenderPostalCode ?? '',
            certSenderCity: data.certSenderCity ?? '', certSenderCountry: data.certSenderCountry ?? D.certSenderCountry,
            certAddressOutput: data.certAddressOutput ?? D.certAddressOutput,
            certLabelPreset: data.certLabelPreset ?? 'custom',
            certLabelFillDirection: data.certLabelFillDirection ?? 'row',
            certLabelWidthMm: data.certLabelWidthMm, certLabelHeightMm: data.certLabelHeightMm,
            certLabelColumns: data.certLabelColumns, certLabelRows: data.certLabelRows,
            certLabelMarginTopMm: data.certLabelMarginTopMm, certLabelMarginLeftMm: data.certLabelMarginLeftMm,
            certLabelGapXMm: data.certLabelGapXMm, certLabelGapYMm: data.certLabelGapYMm,
            certLabelStartPosition: data.certLabelStartPosition,
            certEnvelopeFormat: data.certEnvelopeFormat ?? D.certEnvelopeFormat,
            certErrorNotifyTo: data.certErrorNotifyTo ?? '',
            certNotifyMailEnabled: data.certNotifyMailEnabled, certNotifyMailTo: data.certNotifyMailTo ?? '',
            certNotifyTelegramEnabled: data.certNotifyTelegramEnabled,
            // Secret: starts blank and only overwrites when typed, like every other key here.
            certTelegramBotToken: '', certTelegramChatIds: data.certTelegramChatIds ?? '',
            // ⚠️ Die IBAN wird MIT geladen — sie ist sichtbar, also muss sie auch dastehen.
            // Bei den übrigen Geheimnisfeldern bleibt das Formularfeld leer („leer behält").
            sepaCreditorName: data.sepaCreditorName ?? '', sepaIban: data.sepaIban ?? '', sepaBic: data.sepaBic ?? '',
            currency: data.currency,
        }
        setForm(next); setBaseline(stableJson(next))
    }

    // Load the selected brand's config into the editable brand form.
    function applyBrand(b: BrandConfig) {
        const next = {
            paymentProvider: b.paymentProvider ?? BD.paymentProvider,
            stripeTestmode: b.stripeTestmode,
            stripeTestKey: '', stripeLiveKey: '',
            stripeTestWebhookSecret: '', stripeLiveWebhookSecret: '',
            mollieTestmode: b.mollieTestmode ?? BD.mollieTestmode,
            mollieTestKey: '', mollieLiveKey: '',
            vitalpilzeWebhookUrl: b.vitalpilzeWebhookUrl ?? '', vitalpilzeWebhookSecret: '',
            refundFeeType: b.refundFeeType ?? BD.feeType,
            refundFeeValue: b.refundFeeValue ?? null,
            refundFeeOverrides: (b.refundFeeOverrides ?? []).map((o) => ({ ...o })),
            authorFeeType: b.authorFeeType ?? BD.feeType,
            authorFeeValue: b.authorFeeValue ?? null,
            authorPayoutThreshold: b.authorPayoutThreshold ?? null,
            chargebackFeeFlat: b.chargebackFeeFlat ?? null,
            issuerName: b.issuerName ?? '',
            issuerAddress: b.issuerAddress ?? '',
            issuerTaxNumber: b.issuerTaxNumber ?? '',
            issuerRegister: b.issuerRegister ?? '',
            issuerVatId: b.issuerVatId ?? '',
            issuerStreet: b.issuerStreet ?? '',
            issuerPostalCode: b.issuerPostalCode ?? '',
            issuerCity: b.issuerCity ?? '',
            issuerCountry: b.issuerCountry ?? '',
            issuerEmail: b.issuerEmail ?? '',
            impressumUrl: b.impressumUrl ?? '',
            issuerPhone: b.issuerPhone ?? '',
            brandDisplayName: b.brandDisplayName ?? '',
            contactEmail: b.contactEmail ?? '',
            hotline: b.hotline ?? '',
            mailFromAddress: b.mailFromAddress ?? '',
            mailFromName: b.mailFromName ?? '',
            smtpHost: b.smtpHost ?? '', smtpPort: b.smtpPort ?? null, smtpSecure: !!b.smtpSecure, smtpUser: b.smtpUser ?? '', smtpPass: '',
        }
        setBrandForm(next); setBrandBaseline(JSON.stringify(next))
    }

    useEffect(() => {
        getSettings().then(apply).catch((e) => setError(e?.message ?? T.loadFailed))
        getBrandConfigs().then((list) => {
            setBrands(list)
            setAuthorOn(authorOnOf(list))
            if (list.length) { setSelectedBrand(list[0].brand); applyBrand(list[0]) }
        }).catch(() => {})
    }, [])

    function set<K extends keyof typeof form>(key: K, value: (typeof form)[K]) {
        setForm((f) => ({ ...f, [key]: value }))
    }
    function bset<K extends keyof typeof brandForm>(key: K, value: (typeof brandForm)[K]) {
        setBrandForm((f) => ({ ...f, [key]: value }))
    }

    // Patch one per-article refund fee row in place.
    function bsetOverride(index: number, patch: Partial<RefundFeeOverride>) {
        setBrandForm((f) => ({
            ...f,
            refundFeeOverrides: f.refundFeeOverrides.map((o, i) => (i === index ? { ...o, ...patch } : o)),
        }))
    }

    // Which pane is open right now — 'general' or a brand key. The pill row reads it, and so
    // does the guard below.
    const pane = showBrand ? selectedBrand : GENERAL
    // Is what is currently open unsaved? That, and only that, is what blocks leaving.
    const paneDirty = pane === GENERAL ? globalDirty : brandDirty
    const paneName = pane === GENERAL ? T.general : (selBrand?.name ?? '')

    // Switch pane — blocked while the OPEN one has unsaved changes. Both directions, not just
    // between brands: with one pill row the general settings are a pane like any other, and
    // leaving one that is dirty either overwrites what was typed (another brand: applyBrand
    // replaces brandForm) or hides it (a "Übernehmen" that is lit for something no longer on
    // screen is the sort of unsaved state the ● at the tab exists to prevent).
    function selectPane(target: string) {
        if (target === pane) return
        if (paneDirty) {
            toast(fill(T.saveOrDiscardPane, { pane: paneName }), 'warn')
            return
        }
        if (target === GENERAL) { setShowBrand(false); return }
        setShowBrand(true)
        if (target !== selectedBrand) {
            setSelectedBrand(target)
            const b = brands.find((x) => x.brand === target)
            if (b) applyBrand(b)
        }
    }

    // Throw away what is unsaved in the OPEN pane and return it to the last loaded state.
    function discardPane() {
        if (pane === GENERAL) { if (s) apply(s); setAuthorOn(authorOnOf(brands)) }
        else if (selBrand) applyBrand(selBrand)
    }

    // ── Removing a stored secret ─────────────────────────────────────────────────────────
    // Not part of saving, and that is the whole point: an empty field on save keeps the
    // stored value (that rule is what stops a half-filled form from wiping a live key), so
    // removing needs its own route AND its own gesture. Two things guard against an accident:
    // it never happens implicitly, and it asks first, naming the field and the brand.
    const [toClear, setToClear] = useState<{ scope: 'global' | 'brand', field: string, label: string } | null>(null)
    const [clearBusy, setClearBusy] = useState(false)
    const [clearError, setClearError] = useState('')

    function askClear(scope: 'global' | 'brand', field: string, label: string) {
        // Removing reloads the pane from the server, which would overwrite what is being
        // typed. Same rule as the Stripe webhook button.
        if (dirty) { toast(T.saveBeforeClear, 'warn'); return }
        setClearError('')
        setToClear({ scope, field, label })
    }

    async function confirmClear() {
        if (!toClear) return
        setClearBusy(true); setClearError('')
        try {
            if (toClear.scope === 'global') {
                apply(await clearSettingsSecret(toClear.field))
            } else {
                const updated = await clearBrandSecret(selectedBrand, toClear.field)
                setBrands((bs) => bs.map((b) => (b.brand === updated.brand ? updated : b)))
                applyBrand(updated)
            }
            toast(fill(T.cleared, { label: toClear.label }))
            setToClear(null)
        } catch (e: any) {
            setClearError(e?.response?.data?.error ?? e?.message ?? T.clearFailed)
        } finally {
            setClearBusy(false)
        }
    }

    // One save persists whatever is dirty (global settings and/or the current brand).
    async function save(): Promise<SaveResult> {
        // Required fields and formats from the markup — one check for every form (forms/checkForm.ts).
        const invalid = checkBeforeSave(formRef.current, setError)
        if (invalid) return invalid
        setSaving(true); setError('')
        try {
            if (formDirty) {
                const currencyChanged = !!s && form.currency !== s.currency
                const saved = await saveSettings(form) // empty secrets are kept server-side
                apply(saved)
                // Every list formats with the session's currency — fetch it again.
                if (currencyChanged) await refreshUser()
            }
            // Only the switch is sent: the server merges a patch, every other field of the
            // brand stays as stored.
            for (const b of authorChanged) {
                const updated = await saveBrandConfig(b.brand, { authorSettlementEnabled: authorOn[b.brand] })
                setBrands((bs) => bs.map((x) => (x.brand === updated.brand ? updated : x)))
            }
            if (brandFormDirty && selectedBrand) {
                const updated = await saveBrandConfig(selectedBrand, brandForm)
                setBrands((bs) => bs.map((b) => (b.brand === updated.brand ? updated : b)))
                applyBrand(updated)
            }
            toast(T.saved)
            return { ok: true }
        } catch (e: any) {
            const message = e?.response?.data?.error ?? e?.message ?? T.saveFailed
            setError(message)
            return { ok: false, error: message }
        } finally {
            setSaving(false)
        }
    }

    function onSave(e: React.FormEvent) { e.preventDefault(); void save() }
    useDirtyGuard(tabId, dirty && !readOnly, save)

    // Create/update THIS brand's Stripe webhook endpoint (its own account).
    async function onSetupBrandWebhook() {
        if (!selectedBrand) return
        if (dirty) { toast(T.saveBeforeWebhook, 'warn'); return }
        setBrandWebhookBusy(true); setError('')
        try {
            const r = await setupBrandStripeWebhook(selectedBrand)
            toast(`${r.message} (${r.mode})`)
            const list = await getBrandConfigs()
            setBrands(list)
            const sel = list.find((b) => b.brand === selectedBrand)
            if (sel) applyBrand(sel)
        } catch (e: any) {
            setError(e?.response?.data?.error ?? e?.message ?? T.webhookSetupFailed)
        } finally {
            setBrandWebhookBusy(false)
        }
    }

    if (!s) return <div><h1>{T.title}</h1><p className="muted">{error || T.loading}</p></div>

    return (
        <form ref={formRef} noValidate onSubmit={onSave} className="form-page">
            <div className="form-top">
            <div className="form-header">
                <h1>{readOnly ? T.titleReadOnly : T.title}</h1>
                {!readOnly && <button type="submit" disabled={saving || !dirty}>{saving ? '…' : T.apply}</button>}
                {/* The sentence „Ungespeicherte Änderungen an … — bitte übernehmen oder
                    verwerfen" used to stand under the pills and was removed on request: the ●
                    on the pill already says it, and the toast says it again when a switch is
                    refused. But that sentence also CARRIED the only way to discard — so the
                    control moves up here rather than disappearing with its explanation.
                    Only while there is something to discard. */}
                {!readOnly && paneDirty && (
                    <button type="button" className="secondary" disabled={saving} onClick={discardPane}>
                        {T.discard}
                    </button>
                )}
            </div>

            {readOnly && <div className="notice">{T.readOnlyNotice}</div>}
            {error && <div className="notice err">{error}</div>}

            {/* ONE row: the general settings and the brands side by side. The pill of the open
                pane carries a ● while it is unsaved, and every OTHER pill goes pale and says
                why it cannot be clicked — the same guard the brand row already had, now
                covering "Allgemein" as well. */}
            <div className="pill-tabs" role="tablist">
                {[{ key: GENERAL, label: T.general },
                  ...brands.map((b) => ({ key: b.brand, label: `${b.name}${b.isDefaultBrand ? ' ★' : ''}` }))
                ].map(({ key, label }) => {
                    const active = pane === key
                    const blocked = paneDirty && !active
                    return (
                        <button key={key} type="button" role="tab"
                            className={`pill-tab${active ? ' active' : ''}`}
                            style={blocked ? { opacity: 0.45 } : undefined}
                            title={blocked ? fill(T.paneBlocked, { pane: paneName }) : undefined}
                            onClick={() => selectPane(key)}>
                            {label}{active && paneDirty ? ' ●' : ''}
                        </button>
                    )
                })}
            </div>
            </div>

            <fieldset disabled={readOnly} className="form-body" style={{ border: 0, padding: 0, margin: 0 }}>

            {pane === GENERAL && <>
            <Panel title={CURRENCY_SETTING_TEXTS.title}>
                <div className="field" style={{ maxWidth: 360 }}>
                    <label>{CURRENCY_SETTING_TEXTS.label}</label>
                    <select value={form.currency} required disabled={!!s?.currencyLocked}
                        onChange={(e) => set('currency', e.target.value)}>
                        {currencyOptions.map((c) => <option key={c.code} value={c.code}>{c.code} — {c.name}</option>)}
                    </select>
                    <span className="hint">{s?.currencyLocked ? CURRENCY_SETTING_TEXTS.locked : CURRENCY_SETTING_TEXTS.hint}</span>
                </div>
            </Panel>

            <Panel title={T.checkout.title}>
                <p className="muted" style={{ marginTop: 0 }}>
                    {T.checkout.intro1}<code>POST /api/checkout</code>{T.checkout.intro2}
                    {' '}<code>CHECKOUT_API_KEY</code>{T.checkout.intro3}
                </p>
                <SecretInput
                    name="settings-checkout-api-key"
                    label={T.checkout.apiKey} isSet={s.checkoutApiKeySet} hint={s.checkoutApiKeyHint} 
                    value={form.checkoutApiKey} onChange={(v) => set('checkoutApiKey', v)}
                    onClear={() => askClear('global', 'checkoutApiKey', T.checkout.apiKey)}
                />
            </Panel>

            {/* ⚠️ GLOBAL, nicht je Marke — Entscheidung des Betreibers vom 09.09.2026. Die
                übrigen Aussteller-Angaben (Name, Anschrift, Steuernummern) liegen in der
                MARKEN-Konfiguration; faktisch gibt es aber ein Konto für alles. Ein zweites
                Konto bedeutet später einen Umbau auf markenweise Felder, nicht das Umbiegen
                dieser hier. */}
            <Panel title={T.sepa.title}>
                <h3 style={{ marginTop: 0, marginBottom: 4 }}>{T.sepa.enabledHeading}</h3>
                <p className="muted" style={{ marginTop: 0 }}>{T.sepa.enabledIntro}</p>
                {/* One switch per brand, the brands from the data — never a list here. */}
                <div className="row">
                    {brands.map((b) => (
                        <div className="field" key={b.brand}>
                            <label className="switch">
                                <input type="checkbox" name={`settings-author-enabled-${b.brand}`}
                                    checked={authorOn[b.brand] ?? true}
                                    onChange={(e) => setAuthorOn((m) => ({ ...m, [b.brand]: e.target.checked }))} />
                                {b.name}
                            </label>
                        </div>
                    ))}
                </div>
                <h3 style={{ marginBottom: 4 }}>{T.sepa.bankHeading}</h3>
                <p className="muted" style={{ marginTop: 0 }}>
                    {T.sepa.intro}
                </p>
                <div className="row">
                    <div className="field">
                        <label>{T.sepa.holder}</label>
                        <input type="text" name="settings-sepa-name" autoComplete="off"
                            value={form.sepaCreditorName}
                            onChange={(e) => set('sepaCreditorName', e.target.value)} />
                    </div>
                    <div className="field">
                        <label>{T.sepa.bic} <InfoTip>{T.sepa.bicTip}</InfoTip></label>
                        <input type="text" name="settings-sepa-bic" autoComplete="off"
                            value={form.sepaBic}
                            onChange={(e) => set('sepaBic', e.target.value.toUpperCase())} />
                    </div>
                </div>
                {/* ⚠️ OFFEN, anders als jedes andere Geheimnisfeld auf dieser Seite. Der
                    Betreiber tippt sie hier ein und muss sie prüfen können: „so weiß man nie ob
                    man sich vertippt hat" (09.09.2026). Gespeichert bleibt sie verschlüsselt —
                    geändert hat sich nur die Anzeige.
                    Der Unterschied zu den Schlüsseln darüber ist kein Versehen: Einen API-Schlüssel
                    kann man ersetzen, wenn man ihn nicht mehr kennt. Eine falsch eingetippte IBAN
                    merkt man, wenn das Geld weg ist.
                    Kein „Entfernen"-Knopf: Wer ein sichtbares Feld leert und speichert, löscht es. */}
                <div className="field" style={{ maxWidth: 520 }}>
                    <label>{T.sepa.iban}</label>
                    <input type="text" name="settings-sepa-iban" autoComplete="off" placeholder={T.sepa.ibanPlaceholder}
                        value={form.sepaIban} onChange={(e) => set('sepaIban', e.target.value)} />
                    <span className="hint">
                        {T.sepa.ibanHint}
                    </span>
                </div>
            </Panel>

            <Panel title={T.paypal.title}>
                <div className="field">
                    <label className="switch">
                        <input type="checkbox" checked={form.paypalTestmode}
                            onChange={(e) => set('paypalTestmode', e.target.checked)} />
                        {T.paypal.testmode}
                    </label>
                </div>
                <div className="field">
                    <label>{T.paypal.sandboxClientId}</label>
                    <input type="text" autoComplete="off" placeholder={T.paypal.clientIdPlaceholder}
                        value={form.paypalTestClientId} onChange={(e) => set('paypalTestClientId', e.target.value)} />
                </div>
                <SecretInput
                    name="settings-paypal-test-secret"
                    label={T.paypal.sandboxSecret} isSet={s.paypalTestSecretSet} hint={s.paypalTestSecretHint} 
                    value={form.paypalTestSecret} onChange={(v) => set('paypalTestSecret', v)}
                    onClear={() => askClear('global', 'paypalTestSecret', T.paypal.sandboxSecret)}
                />
                <div className="field">
                    <label>{T.paypal.liveClientId}</label>
                    <input type="text" autoComplete="off"
                        value={form.paypalLiveClientId} onChange={(e) => set('paypalLiveClientId', e.target.value)} />
                </div>
                <SecretInput
                    name="settings-paypal-live-secret"
                    label={T.paypal.liveSecret} isSet={s.paypalLiveSecretSet} hint={s.paypalLiveSecretHint} 
                    value={form.paypalLiveSecret} onChange={(v) => set('paypalLiveSecret', v)}
                    onClear={() => askClear('global', 'paypalLiveSecret', T.paypal.liveSecret)}
                />
            </Panel>

            <Panel title={T.mollieBalance.title}>
                <p className="muted" style={{ marginTop: 0 }}>{T.mollieBalance.intro}</p>
                <SecretInput
                    name="settings-mollie-balance-token"
                    label={T.mollieBalance.token} isSet={s.mollieBalanceTokenSet} hint={s.mollieBalanceTokenHint}
                    placeholder={T.mollieBalance.tokenPlaceholder} info={T.mollieBalance.tokenInfo}
                    value={form.mollieBalanceToken} onChange={(v) => set('mollieBalanceToken', v)}
                    onClear={() => askClear('global', 'mollieBalanceToken', T.mollieBalance.token)}
                />
            </Panel>

            <Panel title={T.smtp.title}>
                <div className="field">
                    <label>{T.smtp.taxAdvisorEmail}</label>
                    <input type="email" value={form.taxAdvisorEmail} onChange={(e) => set('taxAdvisorEmail', e.target.value)} />
                </div>
                <div className="field">
                    <label>{T.smtp.bcc} <InfoTip>{T.smtp.bccTip}</InfoTip></label>
                    <input type="text" name="tax-advisor-bcc" autoComplete="off" value={form.taxAdvisorBcc} onChange={(e) => set('taxAdvisorBcc', e.target.value)} />
                </div>
                <div className="row">
                    <div className="field"><label>{T.smtpHost}</label>
                        <input type="text" value={form.smtpHost} onChange={(e) => set('smtpHost', e.target.value)} /></div>
                    <div className="field"><label>{T.port}</label>
                        <input type="number" value={form.smtpPort} onChange={(e) => set('smtpPort', Number(e.target.value))} style={{ width: 100 }} /></div>
                </div>
                <div className="field">
                    <label className="switch">
                        <input type="checkbox" checked={form.smtpSecure} onChange={(e) => set('smtpSecure', e.target.checked)} />
                        {T.tlsSecure}
                    </label>
                </div>
                <div className="field"><label>{T.smtpUser}</label>
                    <input type="text" autoComplete="off" value={form.smtpUser} onChange={(e) => set('smtpUser', e.target.value)} /></div>
                <SecretInput
                    name="settings-smtp-pass"
                    label={T.smtpPass} isSet={s.smtpPassSet} 
                    value={form.smtpPass} onChange={(v) => set('smtpPass', v)}
                    onClear={() => askClear('global', 'smtpPass', T.smtpPass)}
                />
                <div className="field"><label>{T.smtp.from}</label>
                    <input type="text" value={form.smtpFrom} onChange={(e) => set('smtpFrom', e.target.value)} /></div>
                <div className="field">
                    <label className="switch">
                        <input type="checkbox" checked={s.testMailForced || form.testMailMode} disabled={s.testMailForced}
                            onChange={(e) => set('testMailMode', e.target.checked)} />
                        {T.smtp.testMailMode}
                    </label>
                    <small className="muted">{s.testMailForced
                        ? T.smtp.testMailForced
                        : T.smtp.testMailOn}</small>
                </div>
                <div className="field"><label>{T.smtp.testMailTo}{(s.testMailForced || form.testMailMode) ? ' *' : ''}</label>
                    <input type="email" value={form.testMailTo} required={form.testMailMode && !s.testMailForced} onChange={(e) => set('testMailTo', e.target.value)} />
                    <small className="muted">{T.smtp.testMailToHint}</small></div>
            </Panel>

            <Panel title={T.imap.title}>
                <div className="field">
                    <label className="switch">
                        <input type="checkbox" checked={form.ingestEnabled}
                            onChange={(e) => set('ingestEnabled', e.target.checked)} />
                        {T.imap.enabled}
                    </label>
                </div>
                <div className="row">
                    <div className="field"><label>{T.imap.host}</label>
                        <input type="text" value={form.imapHost} onChange={(e) => set('imapHost', e.target.value)} /></div>
                    <div className="field"><label>{T.port}</label>
                        <input type="number" value={form.imapPort} onChange={(e) => set('imapPort', Number(e.target.value))} style={{ width: 100 }} /></div>
                </div>
                <div className="field">
                    <label className="switch">
                        <input type="checkbox" checked={form.imapSecure} onChange={(e) => set('imapSecure', e.target.checked)} />
                        {T.tlsSecure}
                    </label>
                </div>
                <div className="field"><label>{T.imap.user}</label>
                    <input type="text" autoComplete="off" value={form.imapUser} onChange={(e) => set('imapUser', e.target.value)} /></div>
                <SecretInput
                    name="settings-imap-pass"
                    label={T.imap.pass} isSet={s.imapPassSet} 
                    value={form.imapPass} onChange={(v) => set('imapPass', v)}
                    onClear={() => askClear('global', 'imapPass', T.imap.pass)}
                />
                <div className="row">
                    <div className="field"><label>{T.imap.mailbox}</label>
                        <input type="text" value={form.imapMailbox} onChange={(e) => set('imapMailbox', e.target.value)} /></div>
                    <div className="field"><label>{T.imap.trash}</label>
                        <input type="text" value={form.imapTrashMailbox} onChange={(e) => set('imapTrashMailbox', e.target.value)} /></div>
                </div>
                <div className="field">
                    <label>{T.imap.notify} <InfoTip>{T.imap.notifyTip}</InfoTip></label>
                    <input type="email" value={form.ingestNotifyEmail} onChange={(e) => set('ingestNotifyEmail', e.target.value)} />
                </div>
                <div className="field">
                    <label>{T.imap.adminNotify} <InfoTip>{T.imap.adminNotifyTip}</InfoTip></label>
                    <input type="email" placeholder={form.ingestNotifyEmail || T.imap.sameAsAbove} value={form.adminNotifyEmail}
                        onChange={(e) => set('adminNotifyEmail', e.target.value)} />
                </div>
                <div className="field">
                    <label>{T.imap.publicUrl} <InfoTip>{T.imap.publicUrlTip}</InfoTip></label>
                    <input type="text" placeholder={window.location.origin} value={form.publicBaseUrl}
                        onChange={(e) => set('publicBaseUrl', e.target.value)} />
                    {form.publicBaseUrl !== window.location.origin && (
                        <button type="button" className="link-btn" style={{ marginTop: 6 }}
                            onClick={() => set('publicBaseUrl', window.location.origin)}>
                            {fill(T.imap.useCurrentOrigin, { origin: window.location.origin })}
                        </button>
                    )}
                </div>
            </Panel>

            <Panel title={T.automation.title}>
                <div className="row">
                    <div className="field">
                        <label>{T.automation.aboRunHour} <InfoTip>{fill(T.automation.aboRunHourTip, { max: ABO_RUN_HOUR_MAX })}</InfoTip></label>
                        <input type="number" min={0} max={ABO_RUN_HOUR_MAX} value={form.aboRunHour} style={{ width: 100 }}
                            onChange={(e) => set('aboRunHour', Number(e.target.value))} />
                    </div>
                    <div className="field">
                        <label>{T.automation.pollHours} <InfoTip>{T.automation.pollHoursTip}</InfoTip></label>
                        <input type="number" min={1} max={INGEST_POLL_HOURS_MAX} value={form.ingestPollHours} style={{ width: 100 }}
                            onChange={(e) => set('ingestPollHours', Number(e.target.value))} />
                    </div>
                </div>
                <div className="field">
                    <label className="switch">
                        <input type="checkbox" checked={form.paymentAutoImportEnabled}
                            onChange={(e) => set('paymentAutoImportEnabled', e.target.checked)} />
                        {T.automation.autoImport} <InfoTip>{T.automation.autoImportTip}</InfoTip>
                    </label>
                </div>
                <div className="field">
                    <label>{T.automation.importDay} <InfoTip>{fill(T.automation.importDayTip, { max: PAYMENT_AUTO_IMPORT_DAY_MAX })}</InfoTip></label>
                    <select value={form.paymentAutoImportDay} disabled={!form.paymentAutoImportEnabled}
                        style={{ width: 140 }} onChange={(e) => set('paymentAutoImportDay', Number(e.target.value))}>
                        {Array.from({ length: PAYMENT_AUTO_IMPORT_DAY_MAX }, (_, i) => i + 1).map((d) => (
                            <option key={d} value={d}>{d}.</option>
                        ))}
                    </select>
                </div>
                <div className="field">
                    <label>{T.automation.importTime} <InfoTip>{T.automation.importTimeTip}</InfoTip></label>
                    <input type="time" value={form.paymentAutoImportTime} disabled={!form.paymentAutoImportEnabled}
                        style={{ width: 140 }} onChange={(e) => set('paymentAutoImportTime', e.target.value)} />
                    {s.paymentAutoImportLastPeriod && (
                        <span className="hint" style={{ marginTop: 6 }}>{fill(T.automation.lastImported, { period: s.paymentAutoImportLastPeriod })}</span>
                    )}
                </div>
            </Panel>

            {/* Mailtexte und Mail-Rahmen stehen seit dem 27.09.2026 unter System → Vorlagen → E-Mail. */}

            <Panel title={T.llm.title}>
                <div className="field">
                    <label className="switch">
                        <input type="checkbox" checked={form.llmEnabled}
                            onChange={(e) => set('llmEnabled', e.target.checked)} />
                        {T.llm.enabled} <InfoTip>{T.llm.enabledTip}</InfoTip>
                    </label>
                </div>
                <div className="field">
                    <label>{T.llm.url} <InfoTip>{T.llm.urlTip}</InfoTip></label>
                    <input type="text" placeholder={T.llm.urlPlaceholder} value={form.llmUrl}
                        onChange={(e) => set('llmUrl', e.target.value)} />
                </div>
                <div className="row">
                    <div className="field"><label>{T.llm.model}</label>
                        <input type="text" placeholder={D.llmModel} value={form.llmModel}
                            onChange={(e) => set('llmModel', e.target.value)} /></div>
                    <div className="field"><label>{T.llm.timeout}</label>
                        <input type="number" value={form.llmTimeoutMs} style={{ width: 120 }}
                            onChange={(e) => set('llmTimeoutMs', Number(e.target.value))} /></div>
                </div>
            </Panel>

            <Panel title={T.cert.title}>
                <p className="muted" style={{ marginTop: 0 }}>
                    {T.cert.intro}
                </p>

                <h3 style={{ marginBottom: 4 }}>{T.cert.sender} <InfoTip>
                    {T.cert.senderTip}
                </InfoTip></h3>
                <div className="row">
                    <div className="field"><label>{T.nameCompany}</label>
                        <input type="text" value={form.certSenderName} onChange={(e) => set('certSenderName', e.target.value)} /></div>
                    <div className="field"><label>{T.cert.street}</label>
                        <input type="text" value={form.certSenderLine1} onChange={(e) => set('certSenderLine1', e.target.value)} /></div>
                </div>
                <div className="row">
                    <div className="field"><label>{T.cert.addition}</label>
                        <input type="text" value={form.certSenderLine2} onChange={(e) => set('certSenderLine2', e.target.value)} /></div>
                    <div className="field"><label>{T.postalCode}</label>
                        <input type="text" value={form.certSenderPostalCode} style={{ width: 100 }}
                            onChange={(e) => set('certSenderPostalCode', e.target.value)} /></div>
                    <div className="field"><label>{T.city}</label>
                        <input type="text" value={form.certSenderCity} onChange={(e) => set('certSenderCity', e.target.value)} /></div>
                    <div className="field"><label>{T.country}</label>
                        <CountryPicker name="settings-cert-sender-country" value={form.certSenderCountry}
                            onChange={(code) => set('certSenderCountry', code)} /></div>
                </div>

                <h3 style={{ marginBottom: 4 }}>{T.cert.printOn}</h3>
                <div className="field">
                    <select value={form.certAddressOutput} style={{ width: 280 }}
                        onChange={(e) => set('certAddressOutput', e.target.value as 'label' | 'endless' | 'envelope')}>
                        <option value="label">{T.cert.outputLabel}</option>
                        <option value="endless">{T.cert.outputEndless}</option>
                        <option value="envelope">{T.cert.outputEnvelope}</option>
                    </select>
                </div>

                {form.certAddressOutput !== 'envelope' && (
                    <div className="field">
                        <label>{T.cert.label} <InfoTip>
                            {T.cert.labelTip}
                        </InfoTip></label>
                        <select value={form.certLabelPreset} style={{ width: 340 }}
                            onChange={(e) => set('certLabelPreset', e.target.value)}>
                            {labelPresets.map((p) => <option key={p.key} value={p.key}>{labelPresetText(p)}</option>)}
                            <option value="custom">{T.cert.customPreset}</option>
                        </select>
                    </div>
                )}

                {form.certAddressOutput === 'endless' && (
                    <p className="muted">
                        {T.cert.endlessIntro}{activePreset ? fill(T.cert.endlessSize, { widthMm: activePreset.widthMm, heightMm: activePreset.heightMm }) : ''}.
                    </p>
                )}

                {form.certAddressOutput === 'label' && <>
                    <div className="field">
                        <label>{T.cert.fillDirection} <InfoTip>
                            {T.cert.fillDirectionTip}
                        </InfoTip></label>
                        <select value={form.certLabelFillDirection} style={{ width: 240 }}
                            onChange={(e) => set('certLabelFillDirection', e.target.value as 'row' | 'column')}>
                            <option value="row">{T.cert.fillRow}</option>
                            <option value="column">{T.cert.fillColumn}</option>
                        </select>
                    </div>

                    {activePreset ? (
                        <p className="muted">
                            <strong>{labelPresetText(activePreset)}</strong>{fill(T.cert.presetSummary, { columns: activePreset.columns, rows: activePreset.rows, marginTopMm: activePreset.marginTopMm, marginLeftMm: activePreset.marginLeftMm, gapXMm: activePreset.gapXMm, gapYMm: activePreset.gapYMm })}
                        </p>
                    ) : <>
                    <p className="muted" style={{ marginTop: 0 }}>
                        {fill(T.cert.customIntro, { w: D.certLabelWidthMm, h: D.certLabelHeightMm, columns: D.certLabelColumns, rows: D.certLabelRows, name: textOf(LABEL_PRESET_NAMES, D.certLabelPreset) })}
                    </p>
                    <div className="row">
                        <div className="field"><label>{T.cert.width}</label>
                            <input type="number" step="0.1" value={form.certLabelWidthMm} style={{ width: 100 }}
                                onChange={(e) => set('certLabelWidthMm', Number(e.target.value))} /></div>
                        <div className="field"><label>{T.cert.height}</label>
                            <input type="number" step="0.1" value={form.certLabelHeightMm} style={{ width: 100 }}
                                onChange={(e) => set('certLabelHeightMm', Number(e.target.value))} /></div>
                        <div className="field"><label>{T.cert.columns}</label>
                            <input type="number" min={1} value={form.certLabelColumns} style={{ width: 80 }}
                                onChange={(e) => set('certLabelColumns', Number(e.target.value))} /></div>
                        <div className="field"><label>{T.cert.rows}</label>
                            <input type="number" min={1} value={form.certLabelRows} style={{ width: 80 }}
                                onChange={(e) => set('certLabelRows', Number(e.target.value))} /></div>
                    </div>
                    <div className="row">
                        <div className="field"><label>{T.cert.marginTop}</label>
                            <input type="number" step="0.1" value={form.certLabelMarginTopMm} style={{ width: 110 }}
                                onChange={(e) => set('certLabelMarginTopMm', Number(e.target.value))} /></div>
                        <div className="field"><label>{T.cert.marginLeft}</label>
                            <input type="number" step="0.1" value={form.certLabelMarginLeftMm} style={{ width: 110 }}
                                onChange={(e) => set('certLabelMarginLeftMm', Number(e.target.value))} /></div>
                        <div className="field"><label>{T.cert.gapX}</label>
                            <input type="number" step="0.1" value={form.certLabelGapXMm} style={{ width: 130 }}
                                onChange={(e) => set('certLabelGapXMm', Number(e.target.value))} /></div>
                        <div className="field"><label>{T.cert.gapY}</label>
                            <input type="number" step="0.1" value={form.certLabelGapYMm} style={{ width: 130 }}
                                onChange={(e) => set('certLabelGapYMm', Number(e.target.value))} /></div>
                    </div>
                    </>}

                    {/* Gilt für BEIDE Fälle — die Startposition ist eine Angabe über den Bogen,
                        der gerade im Drucker liegt, kein Maß der Ware. */}
                    <div className="field">
                        <label>{T.cert.startPosition} <InfoTip>
                            {T.cert.startPositionTip}
                        </InfoTip></label>
                        <input type="number" min={1} value={form.certLabelStartPosition} style={{ width: 100 }}
                            onChange={(e) => set('certLabelStartPosition', Number(e.target.value))} />
                    </div>
                </>}

                {form.certAddressOutput === 'envelope' && (
                    <div className="field">
                        <label>{T.cert.envelopeFormat}</label>
                        <select value={form.certEnvelopeFormat} style={{ width: 160 }}
                            onChange={(e) => set('certEnvelopeFormat', e.target.value as 'C4' | 'C5' | 'DL')}>
                            {Object.entries(ENVELOPE_FORMATS).map(([key, size]) => <option key={key} value={key}>{envelopeText(key, size)}</option>)}
                        </select>
                    </div>
                )}

                <h3 style={{ marginBottom: 4 }}>{T.cert.errors} <InfoTip>
                    {T.cert.errorsTip}
                </InfoTip></h3>
                <div className="field">
                    <label>{T.cert.email}</label>
                    <input type="email" value={form.certErrorNotifyTo}
                        placeholder={form.ingestNotifyEmail || T.cert.errorsPlaceholder}
                        onChange={(e) => set('certErrorNotifyTo', e.target.value)} />
                </div>
            </Panel>

            <Panel title={T.certNotify.title}>
                <p className="muted" style={{ marginTop: 0 }}>
                    {T.certNotify.intro}
                </p>

                <div className="field">
                    <label className="switch">
                        <input type="checkbox" checked={form.certNotifyMailEnabled}
                            onChange={(e) => set('certNotifyMailEnabled', e.target.checked)} />
                        {T.certNotify.byMail}
                    </label>
                </div>
                <div className="field">
                    <label>{T.certNotify.recipients} <InfoTip>{T.certNotify.recipientsTip}</InfoTip></label>
                    <input type="text" value={form.certNotifyMailTo} disabled={!form.certNotifyMailEnabled}
                        placeholder={T.certNotify.recipientsPlaceholder}
                        onChange={(e) => set('certNotifyMailTo', e.target.value)} />
                </div>

                <div className="field">
                    <label className="switch">
                        <input type="checkbox" checked={form.certNotifyTelegramEnabled}
                            onChange={(e) => set('certNotifyTelegramEnabled', e.target.checked)} />
                        {T.certNotify.byTelegram}
                    </label>
                </div>
                <p className="muted" style={{ marginTop: 0 }}>
                    <strong>{T.certNotify.telegramAdvice}</strong>{T.certNotify.telegramIntro1}
                    {' '}{T.certNotify.telegramIntro2}
                    {' '}<code>{T.certNotify.telegramUrl}</code>{T.certNotify.telegramIntro3}
                    {' '}<code>message.chat.id</code>{T.certNotify.telegramIntro4}
                </p>
                <div className="row">
                    <SecretInput
                        name="settings-cert-telegram-bot-token"
                        label={T.certNotify.botToken} isSet={s.certTelegramBotTokenSet} hint={s.certTelegramBotTokenHint}
                        disabled={!form.certNotifyTelegramEnabled}
                        value={form.certTelegramBotToken} onChange={(v) => set('certTelegramBotToken', v)}
                        onClear={() => askClear('global', 'certTelegramBotToken', T.certNotify.botToken)}
                    />
                    <div className="field">
                        <label>{T.certNotify.chatIds}</label>
                        <input type="text" disabled={!form.certNotifyTelegramEnabled}
                            placeholder={T.certNotify.chatIdsPlaceholder} value={form.certTelegramChatIds}
                            onChange={(e) => set('certTelegramChatIds', e.target.value)} />
                    </div>
                </div>
            </Panel>

            <Panel title={T.logging.title}>
                <div className="row">
                    <div className="field">
                        <label>{T.logging.defaultLevel} <InfoTip>{T.logging.defaultLevelTip}</InfoTip></label>
                        <select value={form.logLevelDefault} style={{ width: 160 }}
                            onChange={(e) => set('logLevelDefault', e.target.value)}>
                            {LOG_LEVELS.map((l) => <option key={l} value={l}>{l}</option>)}
                        </select>
                    </div>
                    <div className="field">
                        <label>{T.logging.retention} <InfoTip>{T.logging.retentionTip}</InfoTip></label>
                        <input type="number" min={1} max={LOG_RETENTION_DAYS_MAX} value={form.logRetentionDays} style={{ width: 120 }}
                            onChange={(e) => set('logRetentionDays', Number(e.target.value))} />
                    </div>
                </div>
                <label style={{ display: 'block', marginTop: 8, marginBottom: 4 }}>
                    {T.logging.perModule} <InfoTip>{T.logging.perModuleTip}</InfoTip>
                </label>
                <div className="log-modules" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 8 }}>
                    {LOG_MODULES.map((m) => (
                        <div key={m.key} className="field" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, margin: 0 }}>
                            <span>{m.label} <span className="muted">({m.key})</span></span>
                            <select style={{ width: 130 }} value={form.logLevelOverrides[m.key] ?? ''}
                                onChange={(e) => {
                                    const next = { ...form.logLevelOverrides }
                                    if (e.target.value) next[m.key] = e.target.value
                                    else delete next[m.key]
                                    set('logLevelOverrides', next)
                                }}>
                                <option value="">{T.logging.standard}</option>
                                {LOG_LEVELS.map((l) => <option key={l} value={l}>{l}</option>)}
                            </select>
                        </div>
                    ))}
                </div>
            </Panel>
            </>}

            {/* The brand row and its own unsaved-changes notice moved up into the single pill
                row above — a brand is a pane now, not a sub-view. */}
            {showBrand && selBrand && <>
            {(zugferdShown.length > 0 || conditionsShown.length > 0) && (
                <div className="notice warn">
                    {zugferdShown.length > 0 && <div>{fill(R.zugferd, { fields: fieldList(zugferdShown) })}</div>}
                    {conditionsShown.length > 0 && <div>{fill(R.conditions, { fields: fieldList(conditionsShown) })}</div>}
                </div>
            )}
            <Panel title={fill(T.provider.title, { brand: selBrand.name })}>
                <p className="muted" style={{ marginTop: 0 }}>
                    {T.provider.intro}
                </p>
                <div className="field" style={{ maxWidth: 260 }}>
                    <label>{T.provider.active}</label>
                    <select value={brandForm.paymentProvider} disabled={readOnly}
                        onChange={(e) => bset('paymentProvider', e.target.value as 'stripe' | 'mollie')}>
                        <option value="stripe">{T.stripeName}</option>
                        <option value="mollie">{T.mollieName}</option>
                    </select>
                </div>
            </Panel>

            <Panel title={fill(T.mollie.title, { brand: selBrand.name })}>
                <p className="muted" style={{ marginTop: 0 }}>
                    {T.mollie.intro}
                    {' '}<code>{form.publicBaseUrl || window.location.origin}/api/webhook/mollie/{selBrand.brand}</code>
                    {' '}{T.mollie.introEnd}
                </p>
                <div className="field">
                    <label className="switch">
                        <input type="checkbox" checked={brandForm.mollieTestmode}
                            onChange={(e) => bset('mollieTestmode', e.target.checked)} />
                        {T.mollie.testmode}
                    </label>
                </div>
                <SecretInput
                    name="brand-mollie-test-key"
                    label={T.mollie.testKey} isSet={selBrand.mollieTestKeySet} hint={selBrand.mollieTestKeyHint}
                    placeholder={T.mollie.testKeyPlaceholder}
                    value={brandForm.mollieTestKey} onChange={(v) => bset('mollieTestKey', v)}
                    onClear={() => askClear('brand', 'mollieTestKey', T.mollie.testKey)}
                />
                <SecretInput
                    name="brand-mollie-live-key"
                    label={T.mollie.liveKey} isSet={selBrand.mollieLiveKeySet} hint={selBrand.mollieLiveKeyHint}
                    placeholder={T.mollie.liveKeyPlaceholder}
                    value={brandForm.mollieLiveKey} onChange={(v) => bset('mollieLiveKey', v)}
                    onClear={() => askClear('brand', 'mollieLiveKey', T.mollie.liveKey)}
                />
            </Panel>

            <Panel title={fill(T.stripe.title, { brand: selBrand.name })}>
                <p className="muted" style={{ marginTop: 0 }}>
                    {T.stripe.intro}
                    {' '}<code>{form.publicBaseUrl || window.location.origin}/api/webhook/stripe/{selBrand.brand}</code>
                </p>
                <div className="field">
                    <label className="switch">
                        <input type="checkbox" checked={brandForm.stripeTestmode}
                            onChange={(e) => bset('stripeTestmode', e.target.checked)} />
                        {T.stripe.testmode}
                    </label>
                </div>
                <SecretInput
                    name="brand-stripe-test-key"
                    label={T.stripe.testKey} isSet={selBrand.stripeTestKeySet} hint={selBrand.stripeTestKeyHint}
                    placeholder={T.stripe.testKeyPlaceholder}
                    value={brandForm.stripeTestKey} onChange={(v) => bset('stripeTestKey', v)}
                    onClear={() => askClear('brand', 'stripeTestKey', T.stripe.testKey)}
                />
                <SecretInput
                    name="brand-stripe-live-key"
                    label={T.stripe.liveKey} isSet={selBrand.stripeLiveKeySet} hint={selBrand.stripeLiveKeyHint}
                    placeholder={T.stripe.liveKeyPlaceholder}
                    value={brandForm.stripeLiveKey} onChange={(v) => bset('stripeLiveKey', v)}
                    onClear={() => askClear('brand', 'stripeLiveKey', T.stripe.liveKey)}
                />
                <SecretInput
                    name="brand-stripe-test-webhook-secret"
                    label={T.stripe.testWebhookSecret} isSet={selBrand.stripeTestWebhookSecretSet} hint={selBrand.stripeTestWebhookSecretHint}
                    placeholder={T.stripe.webhookSecretPlaceholder}
                    info={T.stripe.testWebhookSecretInfo}
                    value={brandForm.stripeTestWebhookSecret} onChange={(v) => bset('stripeTestWebhookSecret', v)}
                    onClear={() => askClear('brand', 'stripeTestWebhookSecret', T.stripe.testWebhookSecret)}
                />
                <SecretInput
                    name="brand-stripe-live-webhook-secret"
                    label={T.stripe.liveWebhookSecret} isSet={selBrand.stripeLiveWebhookSecretSet} hint={selBrand.stripeLiveWebhookSecretHint}
                    placeholder={T.stripe.webhookSecretPlaceholder}
                    info={T.stripe.liveWebhookSecretInfo}
                    value={brandForm.stripeLiveWebhookSecret} onChange={(v) => bset('stripeLiveWebhookSecret', v)}
                    onClear={() => askClear('brand', 'stripeLiveWebhookSecret', T.stripe.liveWebhookSecret)}
                />
                <div className="field">
                    {/* ⚠️ Knopf und ⓘ stehen in einer `.row`, nicht untereinander. Ursache war
                        NICHT ein Umbruch wegen zu grosser Breite: `.field` ist ein SPALTEN-Flex
                        (`flex-direction: column`), da bekommt jedes Kind seine eigene Zeile und
                        wird nebenbei auf die volle Panelbreite gestreckt. Das ⓘ stand deshalb
                        darunter statt daneben — „Der Tooltip sitzt unter dem Button. Button
                        kleiner machen und würde schon ausreichen" (Betreiber, 12.09.2026).
                        In der Zeile schrumpft der Knopf von selbst auf seine Beschriftung; eine
                        feste Breite waere die Falle, die `.btn-wide` beschreibt. Dasselbe Muster
                        steht in `AuthorStatements.tsx` am Abrechnungsknopf — die Klasse ist
                        vorhanden, sie wurde hier nur nicht benutzt.
                        ⚠️ Der Hinweis „Endpoint eingerichtet" bleibt ein eigenes Kind des
                        `.field` und damit UNTER der Zeile. Er gehoert nicht neben den Knopf: er
                        ist die Bestaetigung, nicht die Beschriftung. */}
                    <div className="row" style={{ gap: 6 }}>
                        <button type="button" onClick={() => void onSetupBrandWebhook()} disabled={brandWebhookBusy}>
                            {brandWebhookBusy ? '…' : T.stripe.setupWebhook}
                        </button>
                        <InfoTip>{T.stripe.setupWebhookTip}</InfoTip>
                    </div>
                    {(brandForm.stripeTestmode ? selBrand.stripeTestWebhookEndpointSet : selBrand.stripeLiveWebhookEndpointSet) && (
                        <span className="hint" style={{ marginTop: 6 }}>{fill(T.stripe.endpointSet, { mode: brandForm.stripeTestmode ? T.test : T.live })}</span>
                    )}
                </div>
            </Panel>

            {selBrand.brand === AUTHOR_BRAND && (
            <Panel title={fill(T.vitalpilze.title, { brand: selBrand.name })}>
                <p className="muted" style={{ marginTop: 0 }}>
                    {T.vitalpilze.intro1}<code>/wp-json/vp/v1/backoffice</code>{T.vitalpilze.intro2}
                </p>
                <div className="field">
                    <label>{T.vitalpilze.url}</label>
                    <input type="url" autoComplete="off" placeholder={T.vitalpilze.urlPlaceholder}
                        value={brandForm.vitalpilzeWebhookUrl} onChange={(e) => bset('vitalpilzeWebhookUrl', e.target.value)} />
                </div>
                <SecretInput
                    name="brand-vitalpilze-webhook-secret"
                    label={T.vitalpilze.secret} isSet={selBrand.vitalpilzeWebhookSecretSet} hint={selBrand.vitalpilzeWebhookSecretHint}
                    placeholder={T.vitalpilze.secretPlaceholder}
                    info={T.vitalpilze.secretInfo}
                    value={brandForm.vitalpilzeWebhookSecret} onChange={(v) => bset('vitalpilzeWebhookSecret', v)}
                    onClear={() => askClear('brand', 'vitalpilzeWebhookSecret', T.vitalpilze.secret)}
                />
            </Panel>
            )}

            {/* A brand without author settlement does not show this panel at all — with
                everything in it, issuer, contact and mail account included (operator,
                2026-10-23: „Das soll ein/ausschaltbar sein. Nicht mehr."). */}
            {!authorsOff && (
            <Panel title={fill(T.author.title, { brand: selBrand.name })}>
                <p className="muted" style={{ marginTop: 0 }}>
                    {T.author.intro1}
                    <b>{T.author.introEither}</b>{T.author.intro2}<b>{T.or}</b>{T.author.intro3}<b>{T.author.introReplaces}</b>{T.author.intro4}
                </p>
                <div className="row">
                    <div className="field" style={{ maxWidth: 160 }}>
                        <label>{T.feeKind}</label>
                        <select value={brandForm.authorFeeType}
                            onChange={(e) => bset('authorFeeType', e.target.value as FeeType)}>
                            <option value="percent">{T.percentOption}</option>
                            <option value="fixed">{fill(T.fixedOption, { sign: moneySign() })}</option>
                        </select>
                    </div>
                    <div {...mark('authorFeeValue', { maxWidth: 160 })}>
                        <label>{T.feeValue} {brandForm.authorFeeType === 'fixed' ? `(${moneySign()})` : '(%)'}</label>
                        <input type="number" step={brandForm.authorFeeType === 'fixed' ? '0.01' : '0.1'}
                            min="0" max={brandForm.authorFeeType === 'fixed' ? undefined : 100}
                            value={brandForm.authorFeeValue ?? ''} placeholder={T.notSet}
                            onChange={(e) => bset('authorFeeValue', e.target.value === '' ? null : Number(e.target.value))} />
                    </div>
                    <div {...mark('authorPayoutThreshold', { maxWidth: 200 })}>
                        <label>{fill(T.author.minPayout, { sign: moneySign() })} <InfoTip>
                            {T.author.minPayoutTip1}<b>{T.author.minPayoutTipDeviation}</b>{T.author.minPayoutTip2}<b>{T.author.minPayoutTipEmptyHere}</b>{T.author.minPayoutTip3}
                        </InfoTip></label>
                        <input type="number" step="0.01" min="0" placeholder={T.notSet}
                            value={brandForm.authorPayoutThreshold ?? ''}
                            onChange={(e) => bset('authorPayoutThreshold', e.target.value === '' ? null : Number(e.target.value))} />
                    </div>
                    <div {...mark('chargebackFeeFlat', { maxWidth: 220 })}>
                        <label>{fill(T.author.chargebackFee, { sign: moneySign() })} <InfoTip>
                            {T.author.chargebackTip1}<b>{T.author.chargebackTipEmpty}</b>{T.author.chargebackTip2}<b>0</b>{T.author.chargebackTip3}
                        </InfoTip></label>
                        {/* Leer bleibt null — niemals still zu 0 machen, das wäre „bewusst keine Gebühr". */}
                        <input type="number" step="0.01" min="0" placeholder={T.notSet}
                            value={brandForm.chargebackFeeFlat ?? ''}
                            onChange={(e) => bset('chargebackFeeFlat', e.target.value === '' ? null : Number(e.target.value))} />
                    </div>
                </div>

                <h3 style={{ marginBottom: 4 }}>{T.issuer.heading} <InfoTip>
                    {T.issuer.tip1}<b>{T.issuer.tipBlock}</b>{T.issuer.tip2}<b>{T.issuer.tipFields}</b>{T.issuer.tip3}
                </InfoTip></h3>
                <div className="row">
                    <div {...mark('issuerName')}>
                        <label>{T.nameCompany}</label>
                        <input type="text" value={brandForm.issuerName} placeholder={T.issuer.namePlaceholder}
                            onChange={(e) => bset('issuerName', e.target.value)} />
                    </div>
                    <div {...mark('issuerTaxNumber', { maxWidth: 200 })}>
                        <label>{T.issuer.taxNumber}</label>
                        <input type="text" value={brandForm.issuerTaxNumber}
                            onChange={(e) => bset('issuerTaxNumber', e.target.value)} />
                    </div>
                    <div {...mark('issuerRegister', { maxWidth: 200 })}>
                        <label>{T.issuer.register}</label>
                        <input type="text" value={brandForm.issuerRegister} placeholder={T.issuer.registerPlaceholder}
                            onChange={(e) => bset('issuerRegister', e.target.value)} />
                    </div>
                    <div {...mark('issuerVatId', { maxWidth: 200 })}>
                        <label>{T.issuer.vatId}</label>
                        <input type="text" value={brandForm.issuerVatId}
                            onChange={(e) => bset('issuerVatId', e.target.value)} />
                    </div>
                </div>
                <div className="field">
                    <label>{T.issuer.addressBlock} <span className="hint">{T.issuer.addressBlockHint}</span></label>
                    <textarea rows={3} value={brandForm.issuerAddress}
                        placeholder={T.issuer.addressBlockPlaceholder}
                        onChange={(e) => bset('issuerAddress', e.target.value)} />
                </div>

                <h3 style={{ marginBottom: 4 }}>{T.issuer.fieldsHeading} <InfoTip>
                    {T.issuer.fieldsTip1}<b>{T.issuer.fieldsTipIso}</b>{T.issuer.fieldsTip2}<b>{T.issuer.fieldsTipNone}</b>{T.issuer.fieldsTip3}
                </InfoTip></h3>
                <div className="row">
                    <div {...mark('issuerStreet')}>
                        <label>{T.issuer.street}</label>
                        <input type="text" value={brandForm.issuerStreet}
                            placeholder={T.issuer.streetPlaceholder}
                            onChange={(e) => bset('issuerStreet', e.target.value)} />
                    </div>
                    <div {...mark('issuerPostalCode', { maxWidth: 120 })}>
                        <label>{T.postalCode}</label>
                        <input type="text" value={brandForm.issuerPostalCode}
                            onChange={(e) => bset('issuerPostalCode', e.target.value)} />
                    </div>
                    <div {...mark('issuerCity')}>
                        <label>{T.city}</label>
                        <input type="text" value={brandForm.issuerCity}
                            onChange={(e) => bset('issuerCity', e.target.value)} />
                    </div>
                    <div {...mark('issuerCountry', { maxWidth: 200 })}>
                        <label>{T.country}</label>
                        <CountryPicker name="brand-issuer-country" value={brandForm.issuerCountry}
                            onChange={(code) => bset('issuerCountry', code)} />
                    </div>
                </div>
                <div className="row">
                    <div {...mark('issuerEmail', { maxWidth: 320 })}>
                        <label>{T.issuer.email} <span className="hint">{T.issuer.emailHint}</span></label>
                        <input type="email" value={brandForm.issuerEmail}
                            placeholder={T.issuer.emailPlaceholder}
                            onChange={(e) => bset('issuerEmail', e.target.value)} />
                    </div>
                    <div className="field" style={{ maxWidth: 240 }}>
                        <label>{T.issuer.phone} <InfoTip>
                            {T.issuer.phoneTip}
                        </InfoTip></label>
                        <input type="tel" value={brandForm.issuerPhone} placeholder={T.issuer.phonePlaceholder}
                            onChange={(e) => bset('issuerPhone', e.target.value)} />
                    </div>
                </div>

                <h3 style={{ marginBottom: 4 }}>{T.contact.heading} <InfoTip>
                    {T.contact.tip1}<b>{T.contact.tipHere}</b>{T.contact.tip2}
                </InfoTip></h3>
                <div className="row">
                    <div className="field">
                        <label>{T.contact.displayName}</label>
                        <input type="text" value={brandForm.brandDisplayName} placeholder={T.contact.displayNamePlaceholder}
                            onChange={(e) => bset('brandDisplayName', e.target.value)} />
                    </div>
                    <div className="field" style={{ maxWidth: 280 }}>
                        <label>{T.contact.email}</label>
                        <input type="email" value={brandForm.contactEmail} placeholder={T.contact.emailPlaceholder}
                            onChange={(e) => bset('contactEmail', e.target.value)} />
                    </div>
                    <div className="field" style={{ maxWidth: 220 }}>
                        <label>{T.contact.hotline} <span className="hint">{T.contact.hotlineHint}</span></label>
                        <input type="tel" value={brandForm.hotline} placeholder={T.contact.hotlinePlaceholder}
                            onChange={(e) => bset('hotline', e.target.value)} />
                    </div>
                </div>
                <div className="field" style={{ maxWidth: 480 }}>
                    <label>{T.contact.impressum} <span className="hint">{T.contact.impressumHint}</span> <InfoTip>
                        {T.contact.impressumTip}
                    </InfoTip></label>
                    <input type="url" value={brandForm.impressumUrl}
                        placeholder={T.contact.impressumPlaceholder}
                        onChange={(e) => bset('impressumUrl', e.target.value)} />
                </div>
                <div className="row">
                    <div className="field" style={{ maxWidth: 320 }}>
                        <label>{T.contact.mailFrom} <span className="hint">{T.contact.mailFromHint}</span> <InfoTip>
                            {T.contact.mailFromTip}
                        </InfoTip></label>
                        <input type="email" value={brandForm.mailFromAddress}
                            placeholder={s?.smtpFrom ? fill(T.emptyEquals, { value: s.smtpFrom }) : T.contact.mailFromDefault}
                            onChange={(e) => bset('mailFromAddress', e.target.value)} />
                    </div>
                    <div className="field" style={{ maxWidth: 280 }}>
                        <label>{T.contact.mailFromName}</label>
                        <input type="text" value={brandForm.mailFromName}
                            onChange={(e) => bset('mailFromName', e.target.value)} />
                    </div>
                </div>

                <h3 style={{ marginBottom: 4 }}>{T.mailAccount.heading} <span className="hint">({
                    selBrand.smtpState === 'own' ? T.mailAccount.own : selBrand.smtpState === 'partial' ? T.mailAccount.partial : T.mailAccount.empty
                })</span> <InfoTip>
                    {T.mailAccount.tip}
                </InfoTip></h3>
                <div className="row">
                    <div className="field"><label>{T.smtpHost}</label>
                        <input type="text" value={brandForm.smtpHost} placeholder={s?.smtpHost ? fill(T.emptyEquals, { value: s.smtpHost }) : ''}
                            onChange={(e) => bset('smtpHost', e.target.value)} /></div>
                    <div className="field" style={{ maxWidth: 120 }}><label>{T.port}</label>
                        <input type="number" value={brandForm.smtpPort ?? ''} placeholder={s?.smtpPort ? fill(T.emptyEquals, { value: String(s.smtpPort) }) : ''}
                            onChange={(e) => bset('smtpPort', e.target.value === '' ? null : Number(e.target.value))} /></div>
                    <div className="field">
                        <label className="switch">
                            <input type="checkbox" checked={brandForm.smtpSecure} onChange={(e) => bset('smtpSecure', e.target.checked)} />
                            {T.tlsSecure}
                        </label>
                    </div>
                </div>
                <div className="field"><label>{T.smtpUser}</label>
                    <input type="text" name="brand-smtp-user" autoComplete="off" value={brandForm.smtpUser}
                        onChange={(e) => bset('smtpUser', e.target.value)} /></div>
                <SecretInput
                    name="brand-smtp-pass"
                    label={T.smtpPass} isSet={!!selBrand.smtpPassSet} hint={selBrand.smtpPassHint}
                    value={brandForm.smtpPass} onChange={(v) => bset('smtpPass', v)}
                    onClear={() => askClear('brand', 'smtpPass', T.smtpPass)}
                />
            </Panel>
            )}

            <Panel title={fill(T.refund.title, { brand: selBrand.name })}>
                <p className="muted" style={{ marginTop: 0 }}>
                    {T.refund.intro1}
                    <b>{T.refund.introLess}</b>{T.refund.intro2}<b>{T.refund.introEither}</b>{T.refund.intro3}<b>{T.or}</b>{T.refund.intro4}
                </p>
                <div className="row">
                    <div className="field" style={{ maxWidth: 160 }}>
                        <label>{T.feeKind}</label>
                        <select value={brandForm.refundFeeType}
                            onChange={(e) => bset('refundFeeType', e.target.value as FeeType)}>
                            <option value="percent">{T.percentOption}</option>
                            <option value="fixed">{fill(T.fixedOption, { sign: moneySign() })}</option>
                        </select>
                    </div>
                    <div {...mark('refundFeeValue', { maxWidth: 160 })}>
                        <label>{T.feeValue} {brandForm.refundFeeType === 'fixed' ? `(${moneySign()})` : '(%)'}</label>
                        <input type="number" step={brandForm.refundFeeType === 'fixed' ? '0.01' : '0.1'}
                            min="0" max={brandForm.refundFeeType === 'fixed' ? undefined : 100}
                            value={brandForm.refundFeeValue ?? ''} placeholder={T.notSet}
                            onChange={(e) => bset('refundFeeValue', e.target.value === '' ? null : Number(e.target.value))} />
                    </div>
                </div>

                <h3 style={{ marginBottom: 4 }}>{T.refund.overrides} <InfoTip>{T.refund.overridesTip}</InfoTip></h3>
                {(brandForm.refundFeeOverrides ?? []).map((o, i) => (
                    <div className="row" key={i} style={{ alignItems: 'flex-end' }}>
                        <div className="field" style={{ maxWidth: 120 }}>
                            <label>{T.refund.itemId}</label>
                            <input type="number" min="1" value={o.itemId || ''}
                                onChange={(e) => bsetOverride(i, { itemId: Number(e.target.value) })} />
                        </div>
                        <div className="field" style={{ maxWidth: 130 }}>
                            <label>{T.feeKind}</label>
                            <select value={o.type} onChange={(e) => bsetOverride(i, { type: e.target.value as FeeType })}>
                                <option value="percent">{T.refund.percent}</option>
                                <option value="fixed">{T.refund.fixed}</option>
                            </select>
                        </div>
                        <div className="field" style={{ maxWidth: 110 }}>
                            <label>{T.feeValue} {o.type === 'fixed' ? `(${moneySign()})` : '(%)'}</label>
                            <input type="number" step={o.type === 'fixed' ? '0.01' : '0.1'}
                                min="0" max={o.type === 'fixed' ? undefined : 100} value={o.value}
                                onChange={(e) => bsetOverride(i, { value: Number(e.target.value) })} />
                        </div>
                        <div className="field">
                            <label>{T.refund.internalLabel}</label>
                            <input type="text" value={o.label} placeholder={T.refund.internalLabelPlaceholder}
                                onChange={(e) => bsetOverride(i, { label: e.target.value })} />
                        </div>
                        <div className="field" style={{ maxWidth: 90 }}>
                            <button type="button" className="secondary" disabled={readOnly}
                                onClick={() => bset('refundFeeOverrides', brandForm.refundFeeOverrides.filter((_, j) => j !== i))}>
                                {T.refund.remove}
                            </button>
                        </div>
                    </div>
                ))}
                <button type="button" className="secondary" disabled={readOnly}
                    onClick={() => bset('refundFeeOverrides', [...(brandForm.refundFeeOverrides ?? []), { itemId: 0, type: BD.feeType as FeeType, value: 0, label: '' }])}>
                    {T.refund.addItem}
                </button>
            </Panel>

            </>}

            </fieldset>

            <ConfirmDialog
                open={!!toClear}
                title={T.clear.title}
                message={toClear ? (
                    <>
                        „{toClear.label}"{toClear.scope === 'brand' && selBrand ? <>{fill(T.clear.ofBrand, { brand: selBrand.name })}</> : null}
                        {' '}{T.clear.deleted}<br />
                        {T.clear.irreversible}
                    </>
                ) : null}
                confirmLabel={T.clear.confirm}
                danger
                busy={clearBusy}
                error={clearError}
                onConfirm={confirmClear}
                onCancel={() => { if (!clearBusy) setToClear(null) }}
            />
        </form>
    )
}
